import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import Button from '../ui/Button';
import labourService from '../../services/labour';
import { errorMessage } from '../../utils/apiResponse';
import { GROUP_FIELDS, type LabourGroupKey, type LabourReport } from '../../types/labour';
import { inputClass } from '../fieldWork/FormModal';
import { DateRangeInputs, Notice } from './shared';
import { hrs, money, unitsText, useDateRange } from './labourUtils';

const GROUPINGS: LabourGroupKey[] = ['block', 'cultivar_area', 'cultivar', 'farm', 'activity', 'worker', 'provider', 'crew', 'job', 'cost_centre', 'date'];
const SPATIAL = new Set<LabourGroupKey>(['farm', 'block', 'cultivar_area']);

/**
 * Hours (and, for a Farm Jobs Admin, cost) by any grouping over a date range.
 * Shared time — a block's hours spread over its cultivar areas — is counted
 * and called out, so nobody reads an estimate as a measurement.
 */
export default function LabourReportPanel() {
  const range = useDateRange();
  const [groupBy, setGroupBy] = useState<LabourGroupKey>('block');
  const [thenBy, setThenBy] = useState<LabourGroupKey | ''>('');
  const [report, setReport] = useState<LabourReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const keys: LabourGroupKey[] = thenBy ? [groupBy, thenBy] : [groupBy];

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    labourService.getLabourReport({ from: range.from, to: range.to }, keys)
      .then(r => { if (live) setReport(r); })
      .catch(e => { if (live) setError(errorMessage(e, 'Could not load the report')); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [range.from, range.to, groupBy, thenBy]); // eslint-disable-line react-hooks/exhaustive-deps

  const costs = !!report?.includes_costs;
  const perHa = keys.length === 1 && SPATIAL.has(groupBy);
  const hasTonnes = !!report?.rows.some(r => (r.est_tonnes ?? 0) > 0);
  const cur = report?.currency ?? 'AUD';
  // The org chooses whether totals are shown with or without tax (contractors bill inc GST).
  const incTax = !!report?.costs_include_tax;
  const taxName = report?.tax_name || 'tax';
  const totalOf = (r: { total?: number; total_inc_tax?: number }) => (incTax ? r.total_inc_tax : r.total);
  const taxNote = `${incTax ? 'inc' : 'ex'} ${taxName}`;
  const label = (row: Record<string, unknown>, k: LabourGroupKey) =>
    (row[GROUP_FIELDS[k].name] as string | null) ?? (k === 'cost_centre' ? '—' : `No ${GROUP_FIELDS[k].label.toLowerCase()}`);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <DateRangeInputs {...range} />
        <label className="text-xs text-neutral-600">
          <span className="block mb-1">Group by</span>
          <select className={inputClass} value={groupBy} onChange={e => setGroupBy(e.target.value as LabourGroupKey)}>
            {GROUPINGS.map(g => <option key={g} value={g}>{GROUP_FIELDS[g].label}</option>)}
          </select>
        </label>
        <label className="text-xs text-neutral-600">
          <span className="block mb-1">Then by</span>
          <select className={inputClass} value={thenBy} onChange={e => setThenBy(e.target.value as LabourGroupKey | '')}>
            <option value="">—</option>
            {GROUPINGS.filter(g => g !== groupBy).map(g => <option key={g} value={g}>{GROUP_FIELDS[g].label}</option>)}
          </select>
        </label>
        <Button size="sm" variant="outline" onClick={() => labourService.downloadLabourCsv({ from: range.from, to: range.to }, keys).catch(e => setError(errorMessage(e, 'Download failed')))}>
          <Download className="h-4 w-4" /> CSV
        </Button>
      </div>

      {error && <Notice tone="error">{error}</Notice>}

      {report && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label="Hours" value={hrs(report.totals.hours)} />
          <Stat label="People" value={String(report.totals.worker_count)} />
          {costs && <Stat label={`Labour cost (${taxNote})`} value={money(totalOf(report.totals), cur)} />}
          {costs && <Stat label="Hours with no rate" value={hrs(report.totals.unrated_hours)} warn={(report.totals.unrated_hours ?? 0) > 0} />}
        </div>
      )}

      {report && report.totals.apportioned_hours > 0 && (
        <Notice tone="info">
          {hrs(report.totals.apportioned_hours)} were recorded against a whole block or farm and are shared across the
          areas in it by hectares. Record crews against a cultivar area to measure it instead.
        </Notice>
      )}
      {costs && (report?.totals.unrated_hours ?? 0) > 0 && (
        <Notice tone="warn">Some time has no rate, so it is counted at zero cost. Add rates on the Rates tab.</Notice>
      )}

      {loading ? (
        <p className="text-sm text-neutral-500 py-6 text-center">Working it out…</p>
      ) : report && report.rows.length === 0 ? (
        <p className="text-sm text-neutral-500 py-6 text-center">No time recorded in this range.</p>
      ) : report && (
        <div className="overflow-x-auto border border-neutral-200 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wide">
              <tr>
                {keys.map(k => <th key={k} className="text-left px-3 py-2 font-medium">{GROUP_FIELDS[k].label}</th>)}
                <th className="text-right px-3 py-2 font-medium">Hours</th>
                <th className="text-right px-3 py-2 font-medium">People</th>
                <th className="text-left px-3 py-2 font-medium">Output</th>
                {hasTonnes && <th className="text-right px-3 py-2 font-medium">Est. t</th>}
                {perHa && <th className="text-right px-3 py-2 font-medium">h/ha</th>}
                {perHa && hasTonnes && <th className="text-right px-3 py-2 font-medium">Est. t/ha</th>}
                {costs && <th className="text-right px-3 py-2 font-medium">Wages</th>}
                {costs && <th className="text-right px-3 py-2 font-medium">Contract ex {taxName}</th>}
                {costs && <th className="text-right px-3 py-2 font-medium">Total {taxNote}</th>}
                {costs && perHa && <th className="text-right px-3 py-2 font-medium">$/ha ex {taxName}</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {report.rows.map((r, i) => (
                <tr key={i}>
                  {keys.map(k => <td key={k} className="px-3 py-2 text-neutral-900 whitespace-nowrap">{label(r, k)}</td>)}
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {hrs(r.hours)}
                    {r.apportioned_hours > 0 && <span className="text-xs text-sky-700" title="Includes time shared by area"> *</span>}
                  </td>
                  <td className="px-3 py-2 text-right">{r.worker_count}</td>
                  <td className="px-3 py-2 text-neutral-600 whitespace-nowrap">{unitsText(r.units)}</td>
                  {hasTonnes && <td className="px-3 py-2 text-right">{r.est_tonnes ? r.est_tonnes.toFixed(2) : '—'}</td>}
                  {perHa && <td className="px-3 py-2 text-right">{r.hours_per_ha ?? '—'}</td>}
                  {perHa && hasTonnes && <td className="px-3 py-2 text-right">{r.est_tonnes_per_ha ?? '—'}</td>}
                  {costs && <td className="px-3 py-2 text-right whitespace-nowrap">{money((r.wage ?? 0) + (r.on_cost ?? 0), cur)}</td>}
                  {costs && <td className="px-3 py-2 text-right whitespace-nowrap">{money(r.charge, cur)}</td>}
                  {costs && <td className="px-3 py-2 text-right whitespace-nowrap font-medium">{money(totalOf(r), cur)}</td>}
                  {costs && perHa && <td className="px-3 py-2 text-right whitespace-nowrap">{r.cost_per_ha == null ? '—' : money(r.cost_per_ha, cur)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {costs && report && (
        <p className="text-xs text-neutral-500">
          Staff and contract columns are ex {taxName}
          {report.on_cost_pct > 0 ? `; staff time includes ${report.on_cost_pct}% on-costs` : ''}
          {(report.totals.tax ?? 0) > 0 ? `. ${taxName} on contract charges: ${money(report.totals.tax, cur)}.` : '.'}
          {' '}$/ha uses the ex-{taxName} total.
        </p>
      )}
    </div>
  );
}

function Stat({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className={`rounded-lg border p-3 ${warn ? 'border-amber-200 bg-amber-50' : 'border-neutral-200 bg-white'}`}>
      <p className="text-xs text-neutral-500">{label}</p>
      <p className="text-lg font-semibold text-neutral-900">{value}</p>
    </div>
  );
}
